import { Apollo } from 'apollo-angular';
import { MaintenanceService } from './maintenance/maintenance.service';
import { maintenanceInterceptor } from './maintenance/maintenance.interceptor';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  Injector,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withNavigationErrorHandler } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import 'moment/locale/uk';
import { environment } from '../environments/environment';
import { navigationErrorHandlerFactory, routes } from './app.routes';
import { LogUpdateService } from './service-worker';

// Firebase imports
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { getStorage, provideStorage } from '@angular/fire/storage';

// Apollo imports

// Application imports
import { FirebaseAuthService } from './entry/auth/firebase-auth.service';
import { AnalyticsService } from './analytics.service';
import { graphqlProvider } from './providers/graphql.provider';
import { GlobalErrorHandler } from './providers/global-error-handler';
import { provideSelectorCopyTool } from './providers/selector-copy-tool.provider';
import { ColorSchemeService } from './services/theme-color-scheme.service';

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular core providers
    importProvidersFrom(
      BrowserModule,
      ServiceWorkerModule.register('main-service.worker.js', {
        enabled: environment.production,
        registrationStrategy: 'registerImmediately'
      }),
      FormsModule,
      ReactiveFormsModule
      // SharedModule, // TODO: Do we still need this?
      // GraphQLModule // <-- REMOVED
    ),

    // System providers
    provideAnimations(),
    provideRouter(
      routes,
      withNavigationErrorHandler((error) => navigationErrorHandlerFactory(inject(Injector))(error))
    ),

    // Material date adapter — locale driven by Angular's LOCALE_ID (set per build locale)
    { provide: MAT_DATE_LOCALE, useFactory: () => inject(LOCALE_ID) },
    provideMomentDateAdapter(),

    // Firebase providers (whenever real auth is used, i.e. not stub/local mode)
    ...(!environment.useStubAuth
      ? [
          importProvidersFrom(provideFirebaseApp(() => initializeApp(environment.firebaseConfig))),
          importProvidersFrom(provideAuth(() => getAuth())),
          importProvidersFrom(provideMessaging(() => getMessaging())),
          importProvidersFrom(providePerformance(() => getPerformance())),
          importProvidersFrom(provideStorage(() => getStorage())),
          importProvidersFrom(provideRemoteConfig(() => getRemoteConfig()))
        ]
      : []),

    provideAppInitializer(() => {
      const maintenance = inject(MaintenanceService);
      const apollo = inject(Apollo);
      maintenance.onRecovery(() => {
        try {
          void apollo.client.reFetchObservableQueries().catch(() => undefined);
        } catch {
          /* No Apollo client yet: the next normal read initializes it. */
        }
      });
      maintenance.start();
    }),

    // Eagerly instantiate side-effect services that must run at app boot
    provideAppInitializer(() => {
      inject(FirebaseAuthService);
    }),
    provideAppInitializer(() => {
      inject(ColorSchemeService);
    }),
    provideAppInitializer(() => {
      inject(LogUpdateService);
    }),
    provideAppInitializer(() => {
      inject(AnalyticsService);
    }),

    // HTTP and feature flag providers
    provideHttpClient(withInterceptors([maintenanceInterceptor]), withInterceptorsFromDi()),
    graphqlProvider,

    // Global error handler — catches unhandled errors and shows a snackbar
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // CSS Selector Copy Tool (local dev only)
    provideSelectorCopyTool()
  ]
};
